import React from 'react';
import { SESSION_TYPE_NAMES } from 'common/src/config';
import { If } from 'common/src/components';
import { lang } from '@umu/ukit-lang';
import { classSet } from '@umu/umu-dom';
import { isInMobile } from '@umu/umu-ua';
import { Iconfont, Tag } from '@umu/umu-design';
import './floatingLayerForContentBaseOfUseMicroPage.scss';
import histroyEmpty from '@umu/umu-search/src/common/images/history-empty.png';
import {
    IAppHistorySearch,
    IAppHistoryCourse,
    ELEMENT_TYPE,
    IAppLenovoCourseExtends,
    IAppSearchData,
    OBJ_TYPE as SEARCH_OBJ_TYPE,
} from '@umu/umu-types';
import {
    SearchRange,
    IAppPopular,
    IAppLenovoElement,
} from '@umu/umu-search/src/searchInputForContent/common/interface';
import { computerHightlight } from '@umu/umu-search/src/common/utils/utils';
import { searchObjLabelMap } from '@umu/umu-search/src/searchInputForContent/common/dataflow';

interface IProps {
    isLogin?: boolean;
    keywords?: string;
    className?: string;
    panelType?: string;
    showType?: string;
    isInIframe?: boolean;
    searchRange: SearchRange;
    historySearch?: IAppHistorySearch[];
    popular?: IAppPopular[];
    historyCourse?: IAppHistoryCourse[];
    LenovoCourse?: IAppLenovoElement[];
    cleanCallback(): void;
    isEnterpriseAccount?: boolean;
    clickCallback(data: IAppSearchData, key: string): void;
    changeSearchRange?: (searchRange: SearchRange, keyword: string) => void;
}

export type ICallBack = (data: IAppSearchData, key: string) => void;

const BubbleCard = ({ data, callBack }: { data: IAppHistorySearch[]; callBack: ICallBack }) => {
    return (
        <div className="float-layer-bubble-card float-row">
            {data &&
                data.map((items: IAppHistorySearch, index: number) => (
                    <span
                        key={'bubble-' + index}
                        className="bubble-span"
                        onClick={() => {
                            callBack(items, 'bubble');
                        }}
                    >
                        <span className="bubble-text">{items.keyword || ''}</span>
                    </span>
                ))}
        </div>
    );
};

const SearchCourseLabel = ({ data }: { data: IAppLenovoElement }) => {
    const objType = (data.obj_type as unknown as SEARCH_OBJ_TYPE) || SEARCH_OBJ_TYPE.group;
    let lable = searchObjLabelMap[objType].label || '';
    if (data.session_type && objType === SEARCH_OBJ_TYPE.element) {
        lable = SESSION_TYPE_NAMES[data.session_type as ELEMENT_TYPE];
    }
    return (
        <div className="label-div">
            <Tag className="lenovo-label">{lable}</Tag>
        </div>
    );
};

const onTextComputerHightlight = (text: string, rate: number = 1) => {
    return computerHightlight({
        width: 500 * rate,
        unitWidth: 15,
        hightlight: text,
    });
};
interface IComputerTextProps {
    label: string;
    list: { title: string }[];
    className?: string;
}
const ComputerText = (props: IComputerTextProps) => {
    const { label, list, className } = props;
    if (list.length === 0) {
        return null;
    }
    const initText = list.map((item: any) => item.title).join(lang('，'));
    return (
        <p className={`circle-text ${className}`}>
            <span>{label}</span>
            <span
                className="truncate-text-1"
                dangerouslySetInnerHTML={{ __html: onTextComputerHightlight(initText) }}
            ></span>
        </p>
    );
};
interface IComputerTagsProps {
    list: { name: string }[];
    className?: string;
}
const ComputerTags = (props: IComputerTagsProps) => {
    const { list, className = '' } = props;
    if (list.length === 0) {
        return null;
    }

    return (
        <ul className={`circle-tags-list ${className}`}>
            {list.map((item: any, index) => {
                return (
                    <li key={`tag-${item.id}-${index}`} className="circle-tags-info">
                        <p
                            dangerouslySetInnerHTML={{ __html: `#${onTextComputerHightlight(item.name)}` }}
                            className="circle-content-row1"
                        />
                    </li>
                );
            })}
        </ul>
    );
};

const ShowContent = ({ extendsData, isPc }: { extendsData: IAppLenovoCourseExtends; isPc: boolean }) => {
    const {
        topic_content,
        course_list = [],
        link_list = [],
        tag_list = [],
    } = extendsData || {
        topic_content: '',
        course_list: [],
        link_list: [],
        tag_list: [],
    };
    const tagList = (tag_list || []).slice(0, isPc ? 10 : 4);

    return (
        <div>
            {topic_content && (
                <p
                    dangerouslySetInnerHTML={{ __html: onTextComputerHightlight(topic_content, 2) }}
                    className="truncate-text-1"
                ></p>
            )}
            <ComputerText
                label={lang('分享课程：')}
                list={course_list}
                className={classSet({
                    'margin-top-8': topic_content !== '',
                    'truncate-text-1': true,
                })}
            />
            <ComputerText
                label={lang('分享链接：')}
                list={link_list}
                className={classSet({
                    'margin-top-8': topic_content !== '' || course_list.length > 0,
                    'truncate-text-1': true,
                })}
            />
            <ComputerTags list={tagList} />
        </div>
    );
};
const SugTitle = ({ data, objType }: { data: any; objType: string }) => {
    const isPc = objType === 'pc';
    const { title, access_code = '', obj_type, extends: extendsData } = data;
    const isCircle = obj_type === SEARCH_OBJ_TYPE.circle;

    if (isCircle && !!extendsData) {
        return (
            <div className="lenovo-content">
                <ShowContent extendsData={extendsData} isPc={isPc} />
            </div>
        );
    }

    return (
        <div className="lenovo-content">
            <span
                className={isPc ? 'lenovo-text' : 'truncate-text-2'}
                dangerouslySetInnerHTML={{ __html: onTextComputerHightlight(title) }}
            />
            <If condition={!!access_code}>
                <span className="lenovo-code">{`${lang('访问码：')} ${access_code}`}</span>
            </If>
        </div>
    );
};

const LenovoCard = ({ data, callBack, types }: { data: IAppLenovoElement[]; callBack: ICallBack; types: string }) => {
    let floatClass = 'float-layer-lenovo';
    floatClass += types === 'phone' ? ' float-lenovo-mobile' : '';

    return (
        <div className={floatClass}>
            {data &&
                data.map((items: IAppLenovoElement, index: number) => {
                    let className = types === 'phone' ? 'lenovo lenovo-mobile' : 'lenovo lenovo-pc';
                    className += index + 1 === data.length ? ' lenovo-list-last' : '';
                    return (
                        <div
                            key={'lenovo-' + index}
                            className={className}
                            onClick={() => {
                                callBack(Object.assign({}, items, { num: index }), 'lenovo');
                            }}
                        >
                            <SearchCourseLabel data={items} />
                            <SugTitle data={items} objType={types} />
                        </div>
                    );
                })}
        </div>
    );
};

const CleanText = ({ types, callBack }: { types: string; callBack: () => void }) => {
    if (types === 'pc') {
        return (
            <div className="float-layer-clear" onClick={callBack}>
                <i className="float-layer-clear-icon" />
                <span>{lang('清空')}</span>
            </div>
        );
    }
    if (types === 'phone') {
        return (
            <div className="float-layer-clear" onClick={callBack}>
                <i className="float-phone-clear-icon" />
            </div>
        );
    }

    return <div />;
};

export const FloatingLayerForContentBaseOfUseMicroPage = (props: IProps) => {
    const {
        isLogin,
        className,
        showType,
        keywords,
        searchRange,
        isInIframe,
        historySearch = [],
        LenovoCourse = [],
        panelType = 'pc',
        isEnterpriseAccount,
        changeSearchRange,
    } = props;

    let classNameFloat = 'float-layer-title float-row';
    if (isInMobile()) {
        classNameFloat += ' float-title-phone';
    }

    const renderSearchRangeInIframe = () => {
        if (searchRange === SearchRange.wholeStation) {
            return (
                <div
                    className="search-range"
                    onClick={() => changeSearchRange?.(SearchRange.currentPage, keywords || '')}
                >
                    <div className="title">{lang('搜索当前页面')}</div>
                    <div className="suffix-icon">
                        <Iconfont type="SmallMore" />
                    </div>
                </div>
            );
        }

        if (searchRange === SearchRange.currentPage && keywords === '') {
            return (
                <div className="search-range" onClick={() => changeSearchRange?.(SearchRange.wholeStation, keywords)}>
                    <div className="prev-icon">
                        <Iconfont type="SmallBack" />
                    </div>
                    <div className="title">{lang('返回全站搜索')}</div>
                </div>
            );
        }

        return (
            <div className="search-range" onClick={() => changeSearchRange?.(SearchRange.wholeStation, keywords || '')}>
                <div className="title">{lang('搜索全站')}</div>
                <div className="suffix-icon">
                    <Iconfont type="SmallMore" />
                </div>
            </div>
        );
    };

    const renderSearchRange = () => {
        if (searchRange === SearchRange.currentPage) {
            return (
                <div
                    className="search-range"
                    onClick={() => changeSearchRange?.(SearchRange.wholeStation, keywords || '')}
                >
                    <div className="title">{lang('搜索全站')}</div>
                    <div className="suffix-icon">
                        <Iconfont type="SmallMore" />
                    </div>
                </div>
            );
        }

        if (searchRange === SearchRange.wholeStation && keywords === '') {
            return (
                <div
                    className="search-range"
                    onClick={() => changeSearchRange?.(SearchRange.currentPage, keywords || '')}
                >
                    <div className="prev-icon">
                        <Iconfont type="SmallBack" />
                    </div>
                    <div className="title">{lang('返回当前页搜索')}</div>
                </div>
            );
        }

        return (
            <div className="search-range" onClick={() => changeSearchRange?.(SearchRange.currentPage, keywords || '')}>
                <div className="title">{lang('搜索当前页面')}</div>
                <div className="suffix-icon">
                    <Iconfont type="SmallMore" />
                </div>
            </div>
        );
    };

    return (
        <div className={`search-input-for-content__use-micro-page__float-layer  ${className}`}>
            <If condition={!!isLogin && !!isEnterpriseAccount}>
                <div className="float-layer__search-range">
                    {isInIframe ? renderSearchRangeInIframe() : renderSearchRange()}
                </div>
            </If>
            <div className="float-layer__panel">
                <If condition={!!(showType && showType === 'history')}>
                    <div className="float-layer__history-panel">
                        <div>
                            <div className="float-layer-bubble-div float-layer-bubble-div__search-history">
                                <div className={classNameFloat}>
                                    <span>{lang('历史搜索')}</span>
                                    <CleanText types={panelType} callBack={props.cleanCallback} />
                                </div>
                                <If condition={historySearch.length === 0}>
                                    <div className="search-history-empty">
                                        <div className="search-history-empty__img">
                                            <img src={histroyEmpty} />
                                        </div>
                                        <div className="search-history-empty__desc">{lang('暂无历史搜索')}</div>
                                    </div>
                                </If>
                                <BubbleCard data={historySearch} callBack={props.clickCallback} />
                            </div>
                        </div>
                    </div>
                </If>
                <If condition={!!(showType && showType === 'lenovo' && LenovoCourse && LenovoCourse.length > 0)}>
                    <LenovoCard data={LenovoCourse} callBack={props.clickCallback} types={panelType} />
                </If>
            </div>
        </div>
    );
};
